// Voice Greeting tab for the Digital Receptionist — type a script, generate it as
// speech (Orpheus TTS, default voice Navi/tara), optionally over a lofi bed you pick
// from a shuffled batch, preview + download, and save it as a selectable greeting.
// Backed by /api/tts (voices / lofi / preview / save-prompt).
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wand2, Download, Save, Music, Shuffle, Sparkles } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from './Toast';

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
type Bed = { id: string; name: string };

// saveMode controls where "Save" writes:
//   'prompt' (default) — the tenant prompt library via POST /api/tts/save-prompt (a
//            selectable IVR greeting). Unchanged legacy behavior.
//   'moh'    — the global Music-on-Hold library via POST /api/system/moh-files/generate.
//            Supports music-only (a bed with no script) as well as spoken-over-lofi.
type GreetingContext = { name?: string; extension?: string; extension_uuid?: string; state_label?: string };

export default function VoiceGreetingPanel({ onSaved, saveMode = 'prompt', context }:
  { onSaved?: (promptUuid?: string) => void; saveMode?: 'prompt' | 'moh'; context?: GreetingContext }) {
  const toast = useToast();
  const isMoh = saveMode === 'moh';
  const meta = useQuery<any>({
    queryKey: ['tts-voices'],
    queryFn: async () => {
      const r = await fetch('/api/tts/voices', { headers: authHeaders() });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
  });

  const [script, setScript] = useState('');
  const [instructions, setInstructions] = useState('');
  const [voice, setVoice] = useState('');
  const [music, setMusic] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  // Id of the take the server cached for the last preview; Save reuses that exact audio
  // instead of generating a new random take (#1293). Cleared whenever the input changes.
  const [previewId, setPreviewId] = useState('');

  // Lofi bed picker — a shuffled batch of instrumental beds to audition + choose from.
  const [beds, setBeds] = useState<Bed[]>([]);
  const [bedUrls, setBedUrls] = useState<Record<string, string>>({});
  const [selectedBed, setSelectedBed] = useState('');
  const [bedsBusy, setBedsBusy] = useState(false);

  const voices: string[] = meta.data?.voices || [];
  const chosenVoice = voice || meta.data?.default || 'tara';
  const hasMusic = !!meta.data?.music;
  const canDraft = meta.data?.draftEnabled !== false;

  async function draftScript() {
    setBusy('draft');
    try {
      const r = await fetch('/api/tts/draft-script', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ context, instructions }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `draft failed (${r.status})`);
      setScript(j.script || '');
    } catch (e: any) { toast('error', e.message); }
    finally { setBusy(''); }
  }

  // Fetch a fresh batch; each bed is pulled as an authed blob so <audio> can play it.
  async function loadBeds() {
    setBedsBusy(true);
    try {
      setBedUrls(prev => { Object.values(prev).forEach(u => URL.revokeObjectURL(u)); return {}; });
      const r = await fetch('/api/tts/lofi?n=6', { headers: authHeaders() });
      const list: Bed[] = (await r.json().catch(() => ({ beds: [] }))).beds || [];
      const urls: Record<string, string> = {};
      await Promise.all(list.map(async (b) => {
        try {
          const br = await fetch(`/api/tts/lofi/${encodeURIComponent(b.id)}`, { headers: authHeaders() });
          if (br.ok) urls[b.id] = URL.createObjectURL(await br.blob());
        } catch { /* skip a bad bed */ }
      }));
      setBeds(list);
      setBedUrls(urls);
      setSelectedBed(list[0]?.id || '');
    } catch (e: any) { toast('error', e.message); }
    finally { setBedsBusy(false); }
  }

  // Load a batch the first time lofi is switched on.
  useEffect(() => {
    if (music && hasMusic && beds.length === 0) loadBeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [music, hasMusic]);

  // Editing anything after a preview means the cached take no longer matches; drop it so
  // Save generates fresh rather than storing audio for a different script/voice/bed.
  useEffect(() => { setPreviewId(''); }, [script, chosenVoice, music, selectedBed]);

  async function generate() {
    setBusy('preview');
    try {
      const r = await fetch('/api/tts/preview', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ script, voice: chosenVoice, music, bed: music ? selectedBed : undefined }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `generation failed (${r.status})`);
      const blob = await r.blob();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(URL.createObjectURL(blob));
      setPreviewId(r.headers.get('X-TTS-Preview-Id') || '');
    } catch (e: any) { toast('error', e.message); }
    finally { setBusy(''); }
  }

  async function save() {
    setBusy('save');
    try {
      const url = isMoh ? '/api/system/moh-files/generate' : '/api/tts/save-prompt';
      const r = await fetch(url, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ script, voice: chosenVoice, music, name, bed: music ? selectedBed : undefined,
          preview_id: previewId || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `save failed (${r.status})`);
      toast('success', isMoh
        ? `Saved "${j.name}" to the hold-music library`
        : `Saved "${j.name}" — pick it as your greeting on the General tab`);
      onSaved && onSaved(j.prompt_uuid);
    } catch (e: any) { toast('error', e.message); }
    finally { setBusy(''); }
  }

  // For MoH, a chosen bed with no script is valid (instrumental hold music). For the
  // prompt library, a script is always required.
  const canSave = isMoh ? (!!script.trim() || (music && !!selectedBed)) : !!script.trim();

  if (meta.isLoading) return <div className="card p-6 text-sm text-textSecondary">Loading…</div>;
  // Speech needs Orpheus. MoH can still generate instrumental music-only when just the
  // lofi library is configured, so don't hard-block that case.
  if (meta.data && meta.data.enabled === false && !(isMoh && hasMusic)) {
    return <div className="card p-6 text-sm text-textSecondary">Voice generation isn’t configured on this server.</div>;
  }

  return (
    <div className="card p-6 space-y-4 max-w-2xl">
      {canDraft && (
        <div className="border border-border rounded-lg p-3 space-y-2">
          <div className="text-sm font-medium flex items-center gap-1"><Sparkles size={14} /> Draft with AI</div>
          {(context?.name || context?.state_label) && (
            <div className="text-xs text-textSecondary">
              {[context.name && `For: ${context.name}`, context.state_label && `Type: ${context.state_label}`].filter(Boolean).join(' · ')}
            </div>
          )}
          <textarea className="input w-full h-16 text-sm" value={instructions}
            onChange={e => setInstructions(e.target.value)}
            placeholder="Optional instructions — e.g. practice name, hours, special message…" />
          <button type="button" className="btn-primary text-sm py-1" disabled={!!busy} onClick={draftScript}>
            <Sparkles size={13} className="inline mr-1" />{busy === 'draft' ? 'Drafting…' : 'Draft script'}
          </button>
          {busy === 'draft' && (
            <div className="h-1 w-full bg-border rounded overflow-hidden">
              <div className="h-full w-full bg-primary animate-pulse" />
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm text-textSecondary mb-1">Greeting script</label>
        <textarea className="input w-full h-28" value={script} maxLength={1500}
          onChange={e => setScript(e.target.value)} placeholder="AI draft will appear here — or type your own…" />
        <div className="text-xs text-textSecondary mt-1">{script.length}/1500</div>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm text-textSecondary mb-1">Voice</label>
          <select className="input" value={chosenVoice} onChange={e => setVoice(e.target.value)}>
            {voices.map(v => <option key={v} value={v}>{v === 'tara' ? 'tara (Navi)' : v}</option>)}
          </select>
        </div>
        {hasMusic && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={music} onChange={e => setMusic(e.target.checked)} />
            <Music size={14} /> Lofi background
          </label>
        )}
      </div>

      {music && hasMusic && (
        <div className="border border-border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Pick a background bed</span>
            <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1" disabled={bedsBusy} onClick={loadBeds}>
              <Shuffle size={13} /> {bedsBusy ? 'Loading…' : 'Shuffle'}
            </button>
          </div>
          {beds.length === 0 && !bedsBusy && <div className="text-xs text-textSecondary">No beds available — add instrumental tracks to the lofi library.</div>}
          <div className="space-y-1.5">
            {beds.map(b => (
              <label key={b.id} className="flex items-center gap-3 text-sm">
                <input type="radio" name="lofibed" checked={selectedBed === b.id} onChange={() => setSelectedBed(b.id)} />
                <span className="w-40 truncate" title={b.name}>{b.name}</span>
                {bedUrls[b.id]
                  ? <audio controls src={bedUrls[b.id]} className="flex-1 h-8" />
                  : <span className="flex-1 text-xs text-textSecondary">preview unavailable</span>}
              </label>
            ))}
          </div>
          <p className="text-xs text-textSecondary">Audition each, pick one, or Shuffle for a new set. The selected bed plays under the greeting.</p>
        </div>
      )}

      <button type="button" className="btn-primary" disabled={!!busy || !script.trim()} onClick={generate}>
        <Wand2 size={14} className="inline mr-1" />{busy === 'preview' ? 'Generating…' : 'Generate & preview'}
      </button>

      {audioUrl && (
        <div className="space-y-2">
          <audio controls src={audioUrl} className="w-full" />
          <a href={audioUrl} download="greeting.wav" className="btn-ghost inline-flex items-center gap-1"><Download size={14} /> Download</a>
        </div>
      )}

      <div className="border-t border-border pt-4 space-y-2">
        <label className="block text-sm text-textSecondary">{isMoh ? 'Save to music library' : 'Save as a greeting'}</label>
        <div className="flex gap-2">
          <input className="input flex-1" value={name} onChange={e => setName(e.target.value)} placeholder={isMoh ? 'e.g. Lobby Hold' : 'e.g. Main Greeting'} />
          <button type="button" className="btn-primary" disabled={!!busy || !canSave || !name.trim()} onClick={save}>
            <Save size={14} className="inline mr-1" />{busy === 'save' ? 'Saving…' : isMoh ? 'Save to Music library' : 'Save as greeting'}
          </button>
        </div>
        <p className="text-xs text-textSecondary">{isMoh
          ? 'Saves to the Music-on-Hold library. Type a script for a spoken announcement, or just pick a bed above for instrumental hold music.'
          : 'Saves to your prompt library, then choose it as the greeting on the General tab.'}</p>
        {previewId && <p className="text-xs text-textSecondary">Save keeps the exact take you just previewed.</p>}
      </div>
    </div>
  );
}
